import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  LayoutDashboard, Wrench, FileText, MessageSquare, ListChecks, Activity,
  Shield, LogOut, Menu, X, Zap, BookOpen, Sparkles, FlaskConical
} from 'lucide-react';

const NAV_DEF = [
  { to: '/', key: 'dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/macchine', key: 'machines', icon: Wrench, testid: 'nav-machines' },
  { to: '/documenti', key: 'documents', icon: FileText, testid: 'nav-documents' },
  { to: '/diagnosi', key: 'chat', icon: MessageSquare, testid: 'nav-chat' },
  { to: '/diagnosi-guidata', key: 'guided', icon: ListChecks, testid: 'nav-guided' },
  { to: '/procedure-patterns', key: 'patterns', icon: Sparkles, testid: 'nav-patterns' },
  { to: '/universal-procedures', key: 'universal', icon: FlaskConical, testid: 'nav-universal' },
  { to: '/field-reports', key: 'field_reports', icon: BookOpen, testid: 'nav-field-reports' },
  { to: '/interventi', key: 'interventions', icon: Activity, testid: 'nav-interventions' },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col w-64 bg-[#0F172A] text-white fixed h-full border-r border-gray-800">
        <div className="px-6 py-6 border-b border-gray-800">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
                <Zap className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-lg leading-none">{t('app.name')}</div>
                <div className="overline text-gray-400 mt-1">{t('app.tagline')}</div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <LanguageSwitcher compact />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4" data-testid="sidebar-nav">
          {NAV_DEF.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.to === '/'}
              data-testid={n.testid}
              className={({ isActive }) => [
                'flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2',
                isActive
                  ? 'bg-white/5 border-[#002FA7] text-white'
                  : 'border-transparent text-gray-400 hover:text-white hover:bg-white/5',
              ].join(' ')}
            >
              <n.icon className="w-4 h-4" strokeWidth={1.8} />
              <span>{t(`nav.${n.key}`)}</span>
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) => [
                'flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 mt-2 pt-4 border-t border-gray-800',
                isActive ? 'bg-white/5 border-[#002FA7] text-white' : 'border-transparent text-gray-400 hover:text-white',
              ].join(' ')}
            >
              <Shield className="w-4 h-4" strokeWidth={1.8} />
              <span>{t('nav.admin')}</span>
            </NavLink>
          )}
        </nav>
        <div className="px-6 py-4 border-t border-gray-800">
          <NavLink to="/profilo" data-testid="nav-profile" className="block hover:bg-white/5 -mx-6 px-6 py-2 transition-colors">
            <div className="text-xs text-gray-400 mb-2">{t('app.logged_as')}</div>
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.full_name}</div>
            <div className="text-xs text-gray-400 truncate">{user?.email}</div>
            <span className="overline mt-1 inline-block text-[#7FB3FF]">{user?.role} · {t('app.edit')} →</span>
          </NavLink>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center gap-2 text-gray-300 hover:text-white text-sm border border-gray-700 px-3 py-2 hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> {t('app.logout')}
          </button>
        </div>
      </aside>

      {/* Mobile Top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0F172A] text-white px-4 py-3 flex items-center justify-between border-b border-gray-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
            <Zap className="w-4 h-4" />
          </div>
          <span className="font-display font-bold">{t('app.name')}</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 top-14 z-30 bg-[#0F172A] text-white overflow-y-auto" data-testid="mobile-menu">
          {NAV_DEF.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-4 border-b border-gray-800 ${isActive ? 'bg-white/10' : ''}`}>
              <n.icon className="w-5 h-5" /> {t(`nav.${n.key}`)}
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <NavLink to="/admin" onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-4 border-b border-gray-800 ${isActive ? 'bg-white/10' : ''}`}>
              <Shield className="w-5 h-5" /> {t('nav.admin')}
            </NavLink>
          )}
          <button data-testid="mobile-logout" onClick={() => { logout(); }} className="w-full text-left flex items-center gap-3 px-6 py-4">
            <LogOut className="w-5 h-5" /> {t('app.logout')}
          </button>
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation bar — always visible on mobile, scroll-independent */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0F172A] border-t border-gray-800 grid grid-cols-5"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {[
          { to: '/', key: 'dashboard', icon: LayoutDashboard, end: true },
          { to: '/diagnosi', key: 'chat', icon: MessageSquare },
          { to: '/diagnosi-guidata', key: 'guided', icon: ListChecks },
          { to: '/procedure-patterns', key: 'patterns', icon: Sparkles },
          { to: '/universal-procedures', key: 'universal', icon: FlaskConical },
        ].map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`mb-${n.key}`}
            className={({ isActive }) => [
              'flex flex-col items-center justify-center py-2 text-[10px] gap-0.5 transition-colors',
              isActive ? 'text-white bg-[#002FA7]/20 border-t-2 border-[#002FA7] -mt-px' : 'text-gray-400 hover:text-white',
            ].join(' ')}
          >
            <n.icon className="w-5 h-5" strokeWidth={1.8} />
            <span className="truncate max-w-full px-1 leading-tight">{t(`nav.${n.key}`)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
